import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface FavoritesState {
  favoriteIds: string[];
}

export const FAVORITES_STORAGE_KEY = 'pos_erp_favorite_menu_items';

// Load from local storage initially
const loadFavorites = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const item = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (item) {
      const parsed = JSON.parse(item);
      // Corrupted/tampered storage must never poison the slice: only an
      // array of strings is accepted, anything else falls back to [].
      if (Array.isArray(parsed)) {
        return parsed.filter((id): id is string => typeof id === 'string');
      }
    }
  } catch {
    // TODO: use a centralized error logger instead of console.error
  }
  return [];
};

/**
 * Pure toggle: reducers must not touch localStorage (impure + double-dispatch
 * under StrictMode would double-write). Callers persist via
 * `toggleFavoritesWithPersistence` BEFORE dispatching this action.
 */
const toggleId = (ids: string[], id: string): string[] => {
  const index = ids.indexOf(id);
  return index === -1 ? [...ids, id] : ids.filter((x) => x !== id);
};

/**
 * Compute the next list and persist it to localStorage. Kept separate from the
 * reducer so the redux state transition stays pure; returns the next list in
 * case the caller wants to display it immediately.
 */
export function toggleFavoritesWithPersistence(id: string, currentIds: string[]): string[] {
  const next = toggleId(currentIds, id);
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Favorites are a convenience: a full or unavailable localStorage (quota,
      // private mode) must not break toggling. The redux state still updates via
      // the toggleFavorite action the caller dispatches alongside this.
    }
  }
  return next;
}

const initialState: FavoritesState = {
  favoriteIds: loadFavorites(),
};

export const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    toggleFavorite: (state, action: PayloadAction<string>) => {
      state.favoriteIds = toggleId(state.favoriteIds, action.payload);
    },
  },
});

export const { toggleFavorite } = favoritesSlice.actions;

export const selectFavoriteIds = (state: { favorites: FavoritesState }) => state.favorites.favoriteIds;
export const selectIsFavorite = (id: string) => (state: { favorites: FavoritesState }) => state.favorites.favoriteIds.includes(id);

export default favoritesSlice.reducer;
